import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag } from 'lucide-react';
type Purchase = {
  name: string;
  city: string;
  province: string;
};
const PURCHASES: Purchase[] = [{
  name: 'María',
  city: 'Córdoba',
  province: 'Córdoba'
}, {
  name: 'Lucas',
  city: 'Rosario',
  province: 'Santa Fe'
}, {
  name: 'Sofía',
  city: 'La Plata',
  province: 'Buenos Aires'
}, {
  name: 'Martín',
  city: 'Mendoza',
  province: 'Mendoza'
}, {
  name: 'Camila',
  city: 'Mar del Plata',
  province: 'Buenos Aires'
}, {
  name: 'Federico',
  city: 'Salta',
  province: 'Salta'
}, {
  name: 'Valentina',
  city: 'San Miguel de Tucumán',
  province: 'Tucumán'
}, {
  name: 'Joaquín',
  city: 'Neuquén',
  province: 'Neuquén'
}, {
  name: 'Agustina',
  city: 'Bahía Blanca',
  province: 'Buenos Aires'
}, {
  name: 'Tomás',
  city: 'San Juan',
  province: 'San Juan'
}, {
  name: 'Florencia',
  city: 'Paraná',
  province: 'Entre Ríos'
}, {
  name: 'Nicolás',
  city: 'Resistencia',
  province: 'Chaco'
}, {
  name: 'Julieta',
  city: 'Posadas',
  province: 'Misiones'
}, {
  name: 'Matías',
  city: 'San Luis',
  province: 'San Luis'
}, {
  name: 'Micaela',
  city: 'Corrientes',
  province: 'Corrientes'
}, {
  name: 'Santiago',
  city: 'Bariloche',
  province: 'Río Negro'
}, {
  name: 'Brenda',
  city: 'Ushuaia',
  province: 'Tierra del Fuego'
}, {
  name: 'Gonzalo',
  city: 'Río Gallegos',
  province: 'Santa Cruz'
}, {
  name: 'Antonella',
  city: 'Santiago del Estero',
  province: 'Santiago del Estero'
}, {
  name: 'Ezequiel',
  city: 'San Salvador de Jujuy',
  province: 'Jujuy'
}, {
  name: 'Rocío',
  city: 'La Rioja',
  province: 'La Rioja'
}, {
  name: 'Bautista',
  city: 'Catamarca',
  province: 'Catamarca'
}, {
  name: 'Carolina',
  city: 'Formosa',
  province: 'Formosa'
}, {
  name: 'Iván',
  city: 'Viedma',
  province: 'Río Negro'
}, {
  name: 'Lara',
  city: 'Comodoro Rivadavia',
  province: 'Chubut'
}, {
  name: 'Franco',
  city: 'Tandil',
  province: 'Buenos Aires'
}, {
  name: 'Daniela',
  city: 'Río Cuarto',
  province: 'Córdoba'
}, {
  name: 'Maximiliano',
  city: 'Villa Carlos Paz',
  province: 'Córdoba'
}, {
  name: 'Belén',
  city: 'Concordia',
  province: 'Entre Ríos'
}, {
  name: 'Manuel',
  city: 'San Rafael',
  province: 'Mendoza'
}];
const TIME_AGOS = ['hace 2 minutos', 'hace 4 minutos', 'hace 7 minutos', 'hace 11 minutos', 'hace 18 minutos', 'hace 23 minutos', 'hace 31 minutos', 'hace 1 hora'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
export function SalesNotifications() {
  const [current, setCurrent] = useState<{
    purchase: Purchase;
    timeAgo: string;
    id: number;
  } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    let idCounter = 0;
    const showNext = () => {
      idCounter += 1;
      setCurrent({
        purchase: pickRandom(PURCHASES),
        timeAgo: pickRandom(TIME_AGOS),
        id: idCounter
      });
      // Hide after 5s, then schedule next in 10-15s
      timeoutId = setTimeout(() => {
        setCurrent(null);
        timeoutId = setTimeout(showNext, 10000 + Math.random() * 5000);
      }, 5000);
    };
    timeoutId = setTimeout(showNext, 8000);
    return () => clearTimeout(timeoutId);
  }, [dismissed]);
  if (dismissed) return null;
  return <div className="fixed bottom-3 left-3 sm:bottom-5 sm:left-5 z-[60] pointer-events-none" aria-live="polite" data-id="element-390">
      <AnimatePresence mode="wait" data-id="element-391">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        x: -30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: -20,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto max-w-[290px] sm:max-w-[330px] bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.35)] ring-1 ring-black/5 overflow-hidden" data-id="element-392">
            <div className="flex items-stretch" data-id="element-393">
              {/* Green accent strip */}
              <div className="w-1.5 bg-emerald-500 flex-shrink-0" data-id="element-394" />

              <div className="flex items-start gap-3 px-3.5 py-3 sm:px-4 sm:py-3.5 flex-1 min-w-0" data-id="element-395">
                {/* Icon badge */}
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.4)]" data-id="element-396">
                  <ShoppingBag size={18} className="text-white" strokeWidth={2.5} data-id="element-397" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-2" data-id="element-398">
                  <p className="text-[12px] sm:text-[13px] text-gray-900 leading-snug" data-id="element-399">
                    <span className="font-bold" data-id="element-400">{current.purchase.name}</span>{' '}
                    <span className="text-gray-600" data-id="element-401">de</span>{' '}
                    <span className="font-bold" data-id="element-402">{current.purchase.city}</span>
                  </p>
                  <p className="text-[11px] sm:text-[12px] text-emerald-600 font-semibold leading-snug mt-0.5" data-id="element-403">
                    Compró el Mega Pack Kinesiología
                  </p>
                  <div className="flex items-center gap-1.5 mt-1" data-id="element-404">
                    <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-405">
                      ✓ Verificada
                    </span>
                    <span className="text-[10px] sm:text-[11px] text-gray-500" data-id="element-406">
                      {current.timeAgo}
                    </span>
                  </div>
                </div>

                {/* Close */}
                <button onClick={() => setDismissed(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors -mr-1" aria-label="Cerrar notificación" data-id="element-407">
                  <X size={15} data-id="element-408" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}